// -----------------------------------------------------------------------------
// <copyright file="global.service.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EMAIL_REGEX,
  EMPTY_GUID,
  PENDO_DEFAULT_ACCOUNT_ID,
  PROJECT_LOOKUP,
  USER_ROLE_KEY,
  WAREHOUSE_ROLES,
} from '../constants/global';
// import { LedgerRow } from '../interfaces/Iledger';

import { HttpHelperService } from './http.service';
import { RolesPermissionsService } from './roles-permissions.service';
import { PendoService } from './pendo.service';
// import { UserPreferenceService } from './user.service';
@Injectable({ providedIn: 'root' })
export class GlobalService {
  // public user;
  private key = CryptoJS.enc.Utf8.parse('7061737323313233');
  private iv = CryptoJS.enc.Utf8.parse('7061737323313233');
  public storageType = 'session';
  private userCredentials = null;
  public dialogErrorSubject = new Subject();
  public showOverlayInDialog = new Subject();
  public showBorderOS = new Subject();
  public showSBBorder = new Subject();
  public updateNotificationCount = new Subject();
  public updateEmail = new Subject();
  public currencyList = [];
  public thousandsSeparator: string = '';
  public decimalSeperator: string = '';
  public isOSR = false;
  public isSalesManager = false;
  public isWarehouseAssociate = false;
  public isQSR = false;
  public userObj = {};
  // private profileDetails;
  public ERPType = 'ERP';
  public IsPFEnabled = false;
  public isWarehouseBranch = false;
  public projectERPList = {};
  public notificationIdList = {};
  public whNotificationSubject = new Subject();
  public maxLatestNotesCount = 3;
  timeFormat = 'YYYY-MM-DD HH:mm:ss';
  public isSuperAdmin = false;
  BidTypeData: { Id: string; Name: string; Order: number }[] = [];
  BidStatusData: { Id: string; Name: string; Order: number }[] = [];
  // public defaultUOM = 'EA';
  private pendoInitialized = false;
  public includeMoreLines = signal(false);
  public existingSPODetails = signal(null);
  public attachSPODetails = signal(null);
  public suppliers = [];
  public suppliersDD = [];
  public PartFGList = [
    { id: '1', text: 'FG-001' },
    { id: '2', text: 'FG-002' },
    { id: '3', text: 'FG-003' },
    { id: '4', text: 'FG-004' },
    { id: '5', text: 'FG-005' },
  ];
  private profileDetails: any;
  public appInitialized = signal(false);
  public customerListData = signal([]);
  public taxonomyListData = signal([]);
  public manufacturersData = signal([]);
  supplierSiteAndShipObj = signal<{ siteList: any[]; attributeList: any[] }>({
    siteList: [],
    attributeList: [],
  });
  constructor(
    private http: HttpHelperService,
    private titleService: Title,
    private router: Router,
    private httpc: HttpClient,
    private zone: NgZone,
    private rolesPermissionService: RolesPermissionsService,
    private pendoService: PendoService,
  ) {
    this.getThousandSeperatorFromLocale();
  }

  public getCustomerListData() {
    return this.customerListData();
  }

  public setCustomerListData(value: any) {
    this.customerListData.update(() => value);
  }

  async getCustomers(): Promise<void> {
    const list = ['includeCustomerList'];
    let queryParam = '';
    list.forEach(
      (key: string) => (queryParam = `${queryParam}${queryParam ? '&' : ''}${key}=true`),
    );
    const customers = await this.http.get('api/LookupDataList?' + queryParam).toPromise();
    this.customerListData.update(() => customers.Customers);
    return customers.Customers;
  }

  public getSupplierSiteAndShip() {
    return this.supplierSiteAndShipObj();
  }

  public setSupplierSiteAndShip(value: any) {
    this.supplierSiteAndShipObj.update(() => value);
  }

  async getSiteAndShipTo(): Promise<void> {
    const list = ['includeSupplierSiteList', 'includeShipToCustomerList'];
    let queryParam = '';
    list.forEach(
      (key: string) => (queryParam = `${queryParam}${queryParam ? '&' : ''}${key}=true`),
    );
    const data = await this.http.get('api/LookupDataList?' + queryParam).toPromise();
    const obj = {
      siteList: data.SupplierSites,
      attributeList: data.ShipToCustomers,
    };
    this.supplierSiteAndShipObj.update(() => obj);
    return data;
  }

  public getTaxonomyListData() {
    return this.taxonomyListData();
  }

  public setTaxonomyListData(value: any) {
    this.taxonomyListData.update(() => value);
  }

  async getTaxonomies(): Promise<void> {
    const list = ['includeTaxonomyList'];
    let queryParam = '';
    list.forEach(
      (key: string) => (queryParam = `${queryParam}${queryParam ? '&' : ''}${key}=true`),
    );
    const res = await this.http.get('api/LookupDataList?' + queryParam).toPromise();
    this.taxonomyListData.update(() => res.Taxonomies);
    return res.Taxonomies;
  }

  public getManufacturersData() {
    return this.manufacturersData();
  }

  public setManufacturersData(value: any) {
    this.manufacturersData.update(() => value);
  }

  async getManufacturers(): Promise<void> {
    const list = ['includeManufacturerList'];
    let queryParam = '';
    list.forEach(
      (key: string) => (queryParam = `${queryParam}${queryParam ? '&' : ''}${key}=true`),
    );
    const res = await this.http.get('api/LookupDataList?' + queryParam).toPromise();
    this.manufacturersData.update(() => res.Manufacturers);
    return res.Manufacturers;
  }

  isLoggedIn(): any {
    const token = this.decryptToken();
    return token ? true : false;
  }

  setToken(token: string): void {
    this.encryptToken(token);
    // const roles = this.getUserDetails()[USER_ROLE_KEY];
    // console.log('roles: ', roles);
  }

  getToken(): any {
    return this.decryptToken();
  }

  setRefreshToken(token: string | null): void {
    this.encryptRefreshToken(token);
  }

  getRefreshToken(): any {
    return this.decryptRefreshToken();
  }
  setCurrency(currency: any): void {
    this.setDataToStorage('currency-list', currency, false);
    this.currencyList = currency;
  }
  getCurrency(): any {
    if (this.getDataFromStorage('currency-list', false, false)) {
      this.currencyList = this.getDataFromStorage('currency-list', false);
    }
    return this.currencyList.length ? this.currencyList : ['$'];
  }
  public setProfileDetail(detail: any) {
    this.profileDetails = detail;
  }

  private encryptToken(token: string): void {
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(token), this.key, {
      keySize: 128 / 8,
      iv: this.iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    this.setDataToStorage('tkn', encrypted.toString(), false);
  }

  private encryptRefreshToken(token: string | null): void {
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(token), this.key, {
      keySize: 128 / 8,
      iv: this.iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    this.setDataToStorage('Rtkn', encrypted.toString(), false);
  }

  private decryptToken(): any {
    const ud = this.getDataFromStorage('tkn', false);
    if (!ud) {
      return;
    }
    const decrypted = CryptoJS.AES.decrypt(ud, this.key, {
      keySize: 128 / 8,
      iv: this.iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
  }

  private decryptRefreshToken(): any {
    const ud = this.getDataFromStorage('Rtkn', false);
    if (!ud) {
      return;
    }
    const decrypted = CryptoJS.AES.decrypt(ud, this.key, {
      keySize: 128 / 8,
      iv: this.iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
  }

  getUserDetails(): any {
    if (this.decryptToken()) {
      const user = this.parseJwt(this.decryptToken());
      return user;
    }
  }

  getInitials(name: string) {
    name = name.replace(/\(.*?\)/g, '').trim();
    if (name.includes(',')) {
      let parts = name.split(',');
      let lastName = parts[0].trim();
      let firstName = parts[1].trim().split(' ')[0];
      return (firstName[0] + lastName[0]).toUpperCase();
    }
    let words = name.split(/[\s,]+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  parseJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );
    return JSON.parse(jsonPayload);
  }

  getDataFromStorage(key: string, isLocal = true, parseRequired = true): any {
    let data = isLocal ? localStorage.getItem(key) : sessionStorage.getItem(key);
    if (data && parseRequired) {
      data = JSON.parse(data);
    }
    return data;
  }
  setDataToStorage(key: string, data: any, isLocal = true) {
    if (isLocal) {
      localStorage.setItem(key, JSON.stringify(data));
    } else {
      sessionStorage.setItem(key, JSON.stringify(data));
    }
  }
  removeFromStorage(key: string, isLocal = true) {
    if (isLocal) {
      localStorage.removeItem(key);
    } else {
      sessionStorage.removeItem(key);
    }
  }

  public clearStorage(): void {
    this.userObj = {};
    localStorage.clear();
    sessionStorage.clear();
  }

  convertEmptyStringsToNull(body: any): any {
    // Recursively traverse the object to replace empty strings with null
    const traverse = (obj: any) => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          if (typeof obj[key] === 'string' && obj[key].trim() === '') {
            obj[key] = null; // Replace empty string with null
          } else if (typeof obj[key] === 'string' && obj[key] !== null) {
            obj[key] = obj[key].trim(); // Trimming the space
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            traverse(obj[key]); // Recursively traverse nested objects
          }
        }
      }
    };

    // Clone the body to avoid mutating the original object
    const modifiedBody = JSON.parse(JSON.stringify(body));
    traverse(modifiedBody);
    return modifiedBody;
  }

  navigateToHomeByView() {
    const userDetails = this.getUserDetails();
    if (userDetails) {
      localStorage.setItem('vendor', JSON.stringify(userDetails.unique_name));
      this.triggerPendoIdentify(true);
      if (this.rolesPermissionService.hasPermission('FinishedGoodsTab', 'VIEW')) {
        this.router.navigate(['/finished-goods']);
      } else if (this.rolesPermissionService.hasPermission('WorkInstructionTab', 'VIEW')) {
        this.router.navigate(['/work-instruction']);
      } else if (this.rolesPermissionService.hasPermission('SalesOrder', 'VIEW')) {
        this.router.navigate(['/so']);
      }
    } else {
      this.router.navigate(['/finished-goods']);
    }
  }

  public triggerPendoIdentify(forceInitialize = false, configuratorDetails?: any) {
    const userDetails = this.getUserDetails();
    if (!userDetails) return;
    if (configuratorDetails) {
      this.pendoInitialized = false;
      userDetails.unique_name = configuratorDetails.loginUserId;
      userDetails.name = configuratorDetails.loginUserId?.split('@')?.[0] ?? null;
    }
    const visitorId: string =
      this.getDataFromStorage('vendor', false, false) ?? userDetails.unique_name;
    const selectedBuId = localStorage.getItem('selected-business-unit');
    const buList: { BusinessUnitId: string; BusinessUnitName: string }[] =
      this.getDataFromStorage('business-units') ?? [];

    let accountId = PENDO_DEFAULT_ACCOUNT_ID;
    if (configuratorDetails) {
      accountId = configuratorDetails.BusinessUnitName;
    } else {
      accountId =
        buList.find((bu) => bu.BusinessUnitId === selectedBuId)?.BusinessUnitName ??
        PENDO_DEFAULT_ACCOUNT_ID;
    }
    if (forceInitialize || !this.pendoInitialized) {
      this.pendoService.initializePendo(visitorId, userDetails, accountId);
      this.pendoInitialized = true;
    } else {
      this.pendoService.reInitializePendo(visitorId, userDetails, accountId);
    }
  }

  public lookupDataListCategories(defaultList?: string[]) {
    const list = defaultList || [
      'includeShipLocationList',
      'includeUserList',
      'includeTypeCodeList',
      'includeSalesLocationList',
      'includeDDPShipLocationsList',
    ];
    let queryParam = '';
    list.forEach(
      (key: string) => (queryParam = `${queryParam}${queryParam ? '&' : ''}${key}=true`),
    );
    return this.http.get('api/LookupDataList?' + queryParam);
  }

  public downloadFileFromContainer(url: string) {
    if (!this.isTrustedBlobUrl(url)) {
      throw new Error('Untrusted URL');
    }
    return this.httpc.get(url, {
      responseType: 'blob',
    });
  }

  public isTrustedBlobUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return (
        parsedUrl.protocol === 'https:' && parsedUrl.hostname.endsWith('.blob.core.windows.net')
      );
    } catch (e) {
      return false; // invalid URL format
    }
  }
  public downloadFile(data: any, fileName?: any) {
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = encodeURI(this.sanitizeUrl(url));
    if (fileName) {
      a.download = fileName;
    }
    a.setAttribute('target', '_blank');
    a.click();
    if (this.getBrowserName() == 'firefox') {
      // temporary fix
      setTimeout(() => this.revokeUrl(url), 10);
    } else {
      window.URL.revokeObjectURL(url);
    }
  }
  sanitizeUrl(url: any) {
    try {
      if (url.startsWith('blob:')) {
        return url;
      }
    } catch (e) {
      // Invalid URL
      console.log('Invalid URL', e);
    }
    return '#'; // Safe fallback
  }
  public getBrowserName(): any {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(window as any).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(window as any).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }
  private revokeUrl(url: any) {
    this.zone.run(() => {
      URL.revokeObjectURL(url);
    });
  }
  convertDate(dt: any) {
    return `${dt.getDate()} ${dt.toLocaleString('en-us', { month: 'short' })} ${dt.getFullYear()}`;
  }

  setZIndex(show?: any) {
    const elements: any = document.getElementsByClassName('elight-box');
    if (elements?.length) {
      elements[0].style.zIndex = '' + (show ? 9999 : 999);
    }
  }
  // public setstorage(name, value): void {
  //     const storage = this.storageType === 'session' ? sessionStorage : localStorage;
  //     storage.setItem(name, JSON.stringify(value));
  // }

  // public getstorage(name): any {
  //     const storage = this.storageType === 'session' ? sessionStorage : localStorage;
  //     return storage.getItem(name);
  // }

  // End common unit and ext price value calculation function-------------------------------------------------------

  private getThousandSeperatorFromLocale() {
    const totn_number = 123456789;
    // let t = totn_number.toLocaleString("nl-NL");
    const t = totn_number.toLocaleString();
    if (t.indexOf(',') > 0) {
      this.thousandsSeparator = ',';
      this.decimalSeperator = '.';
    } else {
      this.thousandsSeparator = '.';
      this.decimalSeperator = ',';
    }
  }

  public hasValidDecimal(num: any, digit?: any) {
    digit = this.isNumber(digit) ? digit : 2;
    num = this.isNumber(num) ? num.toString() : '';
    if (this.decimalSeperator && num.length) {
      const value = num.split(this.decimalSeperator)[1];
      if (value?.length) {
        if (value.length <= digit) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  isNumber(n: any) {
    if (n === '') {
      return false;
    }
    return !isNaN(n);
  }
  formatPrice(inputPrice: any, doNotTrimNum?: boolean) {
    if (!inputPrice && inputPrice !== 0) {
      return null;
    }
    const priceBackup = inputPrice;
    inputPrice = inputPrice.toString().toLowerCase().replace(/,/g, '');
    if (inputPrice && isNaN(inputPrice)) {
      let currSymbol = this.getCurrencySymbol(inputPrice);
      if (currSymbol) {
        if (this.isNumber(inputPrice[0]) && this.isNumber(inputPrice[inputPrice.length - 1])) {
          return null;
        }
        let price = inputPrice.replace(currSymbol, '');
        let negativeSign = '';
        if (price[0] === '-') {
          negativeSign = '-';
          price = price.replace('-', '');
        }
        const spaceIdx = price.indexOf(' ');
        if (spaceIdx === 0 || spaceIdx === price.length - 1) {
          price = price.replace(' ', '');
        } else if (spaceIdx !== -1) {
          return null;
        }
        currSymbol = priceBackup.toString().replace('-', '').replace(/,/g, '').replace(price, '');
        if (this.isNumber(price)) {
          price = parseFloat(price);
          if (doNotTrimNum) {
            price = price.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 10,
            });
          } else {
            price = price.toLocaleString(undefined, {
              minimumFractionDigits: 10,
              maximumFractionDigits: 10,
            });
            price;
            price = this.getPriceWithDecimals(price, 2);
          }
          if (priceBackup.toString().indexOf('-') === 0) {
            price =
              priceBackup.toString().replace('-', '').indexOf(currSymbol) === 0
                ? negativeSign + currSymbol + price
                : negativeSign + price + currSymbol;
          } else {
            price =
              priceBackup.toString().replace('-', '').indexOf(currSymbol) === 0
                ? currSymbol + negativeSign + price
                : negativeSign + price + currSymbol;
          }
          return price;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } else if (this.isNumber(inputPrice)) {
      inputPrice = parseFloat(inputPrice);
      let price = null;
      if (doNotTrimNum) {
        price = inputPrice.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 10,
        });
      } else {
        price = inputPrice.toLocaleString(undefined, {
          minimumFractionDigits: 10,
          maximumFractionDigits: 10,
        });
        price = this.getPriceWithDecimals(price, 2);
      }

      return this.currencyList[0] + price;
    } else {
      return null;
    }
  }
  private getPriceWithDecimals(price: any, decimals: number) {
    const split = price.toString().split('.');
    if (split.length > 1) {
      return [split[0], split[1].substring(0, decimals)].join('.');
    } else {
      return [split[0], '00'].join('.');
    }
  }

  public getCurrencySymbol(price: any) {
    let currSymbol = null;
    for (const cur of this.getCurrency()) {
      // getting the currency symbol from the price ex: $10 then getting the $
      if (price.indexOf(cur.toLowerCase()) > -1) {
        currSymbol = cur.toLowerCase();
        break;
      }
    }
    return currSymbol;
  }

  isBusinessUnitSelected() {
    const businessUnits = this.getDataFromStorage('business-units');
    const selectedBusinessUnit = this.getDataFromStorage('selected-business-unit', true, false);
    if (!selectedBusinessUnit && businessUnits?.length > 1) {
      return false;
    }
    return true;
  }

  getOnlyValue(value: any) {
    if (value || value === 0) {
      value = value.toString().toLowerCase().replace(/,/g, '');
    } else {
      return null;
    }
    if (value && isNaN(value)) {
      // if price is string
      const currSymbol = this.getCurrencySymbol(value);
      if (currSymbol) {
        if (this.isNumber(value[0]) && this.isNumber(value[value.length - 1])) {
          return null;
        }
        let formattedCost = value.replace(currSymbol, '');
        formattedCost = formattedCost.trim();
        if (this.isNumber(formattedCost)) {
          return parseFloat(formattedCost);
        } else {
          return null;
        }
      } else {
        return null;
      }
    } else if (this.isNumber(value)) {
      return parseFloat(value);
    } else {
      return null;
    }
  }

  formatMargin(
    margin: string | number | null | undefined,
    shouldRound: boolean = false,
    digits?: number,
  ): string | null {
    if (
      margin === null ||
      margin === undefined ||
      margin === Infinity ||
      margin === -Infinity ||
      margin === 100
    ) {
      return null;
    }
    const cleanStr = margin.toString().replace(/%/g, '').trim();
    if (cleanStr === '') return null;

    const num = Number(cleanStr);
    if (isNaN(num)) return null;
    if (num === 0) return '0.00%';
    if (!digits) {
      digits = 2;
    }
    let processedNum: number;
    if (shouldRound) {
      const multiplier = Math.pow(10, digits);
      processedNum = Math.round((num + Number.EPSILON) * multiplier) / multiplier;
    } else {
      const regex = new RegExp(`^-?\\d+(?:\\.\\d{0,${digits}})?`);
      const matched = cleanStr.match(regex);
      processedNum = matched && matched[0] ? parseFloat(matched[0]) : num;
    }
    return (
      processedNum.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: digits,
      }) + '%'
    );
  }

  getOnlyMarginValue(margin: any) {
    if (margin) {
      const lastChar = margin.toString().charAt(margin.length - 1);
      if (lastChar === '%') {
        margin = +margin.substring(0, margin.length - 1);
      }
    }
    margin = margin ? margin : 0;
    return margin;
  }

  public generateGuid() {
    let dt = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      let rnd = Math.random() * 16; //random number in range 0 to 16
      rnd = ((dt + rnd) % 16) | 0;
      dt = Math.floor(dt / 16);
      return (c === 'x' ? rnd : (rnd & 0x3) | 0x8).toString(16);
    });
  }

  public convertAndAddHoursToDate(dateString: string) {
    const date = dateString ? new Date(dateString).setHours(12) : null;
    return date ? new Date(date) : null;
  }
}
